import { handleErrorToast, handleWarningToast } from 'src/components/molecules';
import theme from 'src/theme/theme';
import { UploadScanStatus } from 'src/types';
import { DefaultTheme } from 'styled-components';

/**
 * Determines if a download should be blocked based on scan status.
 * @returns true if download should be blocked
 */
export const shouldBlockDownload = (
  scanStatus: string | null | undefined,
): boolean => {
  return (
    scanStatus === UploadScanStatus.INFECTED ||
    scanStatus === UploadScanStatus.PENDING
  );
};

/**
 * Returns appropriate error message for blocked downloads.
 */
export const getScanStatusErrorMessage = (
  scanStatus: string | null | undefined,
): string => {
  if (scanStatus === UploadScanStatus.INFECTED) {
    return 'This file has been flagged by security check and cannot be downloaded.';
  }
  if (scanStatus === UploadScanStatus.PENDING) {
    return 'This file is currently being scanned and cannot be downloaded yet.';
  }
  return 'This file cannot be downloaded due to its security status.';
};

/**
 * Checks if a file can be downloaded based on its scan status.
 * Shows appropriate toast messages for blocked downloads.
 * @returns true if download should proceed, false if blocked
 */
export const canDownloadFile = (
  scanStatus: UploadScanStatus | string | null | undefined,
): boolean => {
  if (scanStatus === UploadScanStatus.PENDING) {
    handleWarningToast({
      toastMessage: 'Download blocked',
      toastDescription:
        'This file is currently being scanned and cannot be downloaded yet.',
    });
    return false;
  }

  if (scanStatus === UploadScanStatus.INFECTED) {
    handleErrorToast({
      toastMessage: 'Download blocked',
      toastDescription:
        'This file has been flagged by security check and cannot be downloaded.',
    });
    return false;
  }

  if (
    scanStatus !== null &&
    scanStatus !== undefined &&
    scanStatus !== UploadScanStatus.CLEAN &&
    scanStatus !== UploadScanStatus.MANUALLY_APPROVED
  ) {
    handleErrorToast({
      toastMessage: 'Download blocked',
      toastDescription:
        'This file cannot be downloaded due to its security status.',
    });
    return false;
  }

  return true;
};

/**
 * Returns the appropriate color for a scan status.
 */
export const getScanStatusColor = (
  scanStatus: UploadScanStatus | string | null | undefined,
  t?: DefaultTheme,
): string => {
  if (t && !t.isVaultVariant) {
    switch (scanStatus) {
      case UploadScanStatus.INFECTED:
        return t.atlas.semantic.color.action.destructive.default;
      case UploadScanStatus.CLEAN:
      case UploadScanStatus.MANUALLY_APPROVED:
      case null:
        return t.atlas.semantic.color.action.primary.default;
      case UploadScanStatus.PENDING:
        return t.atlas.semantic.color.status.warning.text;
      default:
        return t.atlas.semantic.color.type.default;
    }
  }
  switch (scanStatus) {
    case UploadScanStatus.INFECTED:
      return theme.newPalette.red.R300;
    case UploadScanStatus.CLEAN:
    case UploadScanStatus.MANUALLY_APPROVED:
    case null:
      return theme.newPalette.turquoise.T300;
    case UploadScanStatus.PENDING:
      return theme.newPalette.yellow.Y500;
    default:
      return theme.newPalette.neutral.N600;
  }
};

/**
 * Determines the worst scan status from an array of uploads.
 * Priority: INFECTED > PENDING > CLEAN > null
 */
export const getWorstScanStatus = (
  uploads: Array<{ scanStatus?: string | null }> | null | undefined,
): UploadScanStatus | null => {
  if (!uploads?.length) return null;

  const statuses = uploads.map((u) => u.scanStatus);
  const hasUnscanned = statuses.some((s) => s === null || s === undefined);

  if (statuses.includes(UploadScanStatus.INFECTED)) {
    return UploadScanStatus.INFECTED;
  }
  if (statuses.includes(UploadScanStatus.PENDING) || hasUnscanned) {
    return UploadScanStatus.PENDING;
  }
  if (
    statuses.every(
      (s) =>
        s === UploadScanStatus.CLEAN ||
        s === UploadScanStatus.MANUALLY_APPROVED,
    )
  ) {
    return UploadScanStatus.CLEAN;
  }
  return null;
};

/**
 * Returns the status column text for display in evidence tables.
 */
export const getStatusColumnText = (
  scanStatus: UploadScanStatus | null,
  statusTexts: {
    pendingUpload: string;
    passedUpload: string;
    infectedUpload: string;
    readyForDownload: string;
  },
  downloadEvidenceDisabled?: boolean,
  isReporterFacing?: boolean,
): string => {
  switch (scanStatus) {
    case UploadScanStatus.INFECTED:
      return statusTexts.infectedUpload;
    case UploadScanStatus.CLEAN:
    case UploadScanStatus.MANUALLY_APPROVED:
    case null:
      return downloadEvidenceDisabled && isReporterFacing
        ? statusTexts.passedUpload
        : statusTexts.readyForDownload;
    case UploadScanStatus.PENDING:
      return statusTexts.pendingUpload;
    default:
      return '';
  }
};
